<template>
  <v-container fluid>
    <v-progress-linear v-if="loading" indeterminate />
    <template v-if="quote">
      <div class="d-flex align-center flex-wrap mb-2 detalle__header">
        <v-btn icon to="/cotizaciones" aria-label="Volver"><v-icon>mdi-arrow-left</v-icon></v-btn>
        <h2 class="text-h5 mr-2">{{ quote.clientName }}</h2>
        <span class="text-caption mr-2">ID {{ quote.proposalCode }} · versión {{ quote.version }}</span>
        <v-chip small outlined>{{ statusLabel(quote.status) }}</v-chip>
        <v-spacer />
        <v-btn v-if="quote.proposalUrl" text :href="quote.proposalUrl" target="_blank" rel="noopener">
          <v-icon left small>mdi-open-in-new</v-icon>Ver propuesta
        </v-btn>
        <v-btn v-if="actions.editDraft && can('quote-builder:update')" text :to="`/cotizador/${quote.id}`">Editar</v-btn>
        <v-btn v-if="actions.send && canOnRecord('quotes:send-proposal', quote.createdBy)" color="primary" :loading="working" @click="generateProposal">Generar Propuesta</v-btn>
        <v-btn v-if="actions.editVersion && canOnRecord('quotes:update', quote.createdBy)" text :disabled="working" @click="editOpen = true">Editar</v-btn>
        <v-btn v-if="actions.markClosed && canOnRecord('quotes:update', quote.createdBy)" text :disabled="working" @click="lostOpen = true">Perdida</v-btn>
        <v-btn v-if="actions.markClosed && canOnRecord('quotes:update', quote.createdBy)" color="primary" :loading="working" @click="changeStatus('won')">Ganada</v-btn>
      </div>

      <v-alert v-if="openDraft" type="info" text dense>
        Esta cotización ya tiene una versión {{ openDraft.version }} en borrador.
        <router-link v-if="can('quote-builder:update')" :to="`/cotizador/${openDraft.id}`">Abrirla</router-link>
      </v-alert>

      <v-row>
        <v-col cols="12" md="8">
          <div class="pw-label mb-2">Propuesta para {{ quote.quantity.toLocaleString('es-CL') }} {{ quote.line === 'asistencias' ? 'asegurados' : 'colaboradores' }} · Valores Mensuales Netos {{ quote.currency }}</div>
          <h4 class="text-subtitle-1 font-weight-bold mb-2">{{ poolLines.length ? 'Planes Colectivos' : 'Planes' }}</h4>
          <quote-result
            :lines="collectiveLines"
            :currency="quote.currency"
            :commission-pct="quote.commissionPct"
            :is-assistance="quote.line === 'asistencias'"
              :show-meta="false"
          />
          <template v-if="poolLines.length">
            <h4 class="text-subtitle-1 font-weight-bold mt-6 mb-2">
              Planes Flexibles | Bolsa {{ quote.input.poolSize.toLocaleString('es-CL') }} Colaboradores | {{ poolPctText }}
            </h4>
            <quote-result
              :lines="poolLines"
              :currency="quote.currency"
              :commission-pct="quote.commissionPct"
                  :show-meta="false"
              />
          </template>
        </v-col>
        <v-col cols="12" md="4">
          <v-card outlined class="mb-3">
            <v-card-text>
              <div class="pw-label">Empresa</div>
              <div class="pw-value mb-2">
                <a v-if="quote.pipedriveUrl" :href="quote.pipedriveUrl" target="_blank" rel="noopener">{{ quote.clientName }} en Pipedrive</a>
                <span v-else>{{ quote.clientName }}</span>
              </div>
              <div class="pw-label">Negocio</div>
              <div class="pw-value mb-2">
                {{ countryLabel(quote.countryIso) }} · {{ lineLabel(quote.line) }} · {{ channelLabel(quote.channel) }}<span v-if="quote.brokerName"> · {{ quote.brokerName }}</span>
              </div>
              <div class="pw-label">Cantidad</div>
              <div class="pw-value mb-2">{{ quote.quantity.toLocaleString('es-CL') }}</div>
              <div class="pw-label">Descuento</div>
              <div class="pw-value mb-2">{{ pct(quote.discountPct) }}</div>
              <div class="pw-label">Calculada con</div>
              <div class="text-body-2">
                Parámetros {{ parameterVersionLabel }} · dólar {{ rate('CLP') }}
                <span v-if="quote.currency !== 'CLP'"> · {{ quote.currency }} {{ rate(quote.currency) }}</span>
              </div>
              <div v-if="quote.notes" class="mt-2">
                <div class="pw-label">Notas</div>
                <div class="text-body-2">{{ quote.notes }}</div>
              </div>
            </v-card-text>
          </v-card>

          <v-card outlined class="mb-3">
            <v-card-title class="text-subtitle-1">Versiones</v-card-title>
            <v-list dense>
              <v-list-item
                v-for="version in quote.versions"
                :key="version.id"
                :to="`/cotizaciones/${version.id}`"
                :input-value="version.id === quote.id"
              >
                <v-list-item-content>
                  <v-list-item-title>Versión {{ version.version }} · {{ statusLabel(version.status) }}</v-list-item-title>
                  <v-list-item-subtitle>{{ version.createdAt | moment('DD-MM-YYYY') }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>

          <v-card outlined>
            <v-card-title class="text-subtitle-1">Historial</v-card-title>
            <v-list dense>
              <v-list-item v-for="(event, index) in quote.events" :key="index">
                <v-list-item-content>
                  <v-list-item-title>{{ eventLabel(event) }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ executiveName(event.actorId) }} · {{ event.createdAt | moment('DD-MM-YYYY HH:mm') }}
                  </v-list-item-subtitle>
                  <div v-if="event.reason" class="text-body-2">{{ reasonText(event) }}</div>
                  <div v-if="event.comment" class="text-body-2">“{{ event.comment }}”</div>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <v-dialog v-model="lostOpen" max-width="480">
      <v-card>
        <v-card-title class="text-h6">¿Por qué se perdió?</v-card-title>
        <v-card-text>
          <v-select v-model="lostReason" :items="lostReasons" label="Motivo" dense outlined />
          <v-textarea v-model="lostComment" label="Comentario (opcional)" rows="2" auto-grow outlined />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="lostOpen = false">Cancelar</v-btn>
          <v-btn color="error" :disabled="!lostReason" :loading="working" @click="markLost">Marcar perdida</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editOpen" max-width="480">
      <v-card>
        <v-card-title class="text-h6">¿Qué pidió cambiar el cliente?</v-card-title>
        <v-card-text>
          <p class="text-body-2">Se abre la versión {{ quote ? quote.versions.length + 1 : '' }} con todo copiado; la versión enviada queda guardada.</p>
          <v-select v-model="changeReason" :items="changeReasons" label="Qué pidió" dense outlined />
          <v-textarea v-model="changeComment" label="Detalle" rows="2" auto-grow outlined />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="editOpen = false">Cancelar</v-btn>
          <v-btn color="primary" :disabled="!changeReason" :loading="working" @click="newVersion">Editar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" top>{{ snackbarMessage }}</v-snackbar>
  </v-container>
</template>

<script>
import QuoteResult from '../components/QuoteResult.vue';
import B2bQuotesService from '../services/b2b-quotes.service';
import EjecutivosService from '../services/ejecutivos.service';
import { utilsMixin } from '../mixins/utilsMixin';
import { can, canOnRecord } from '../utils/permissions';
import {
  CHANNEL_LABELS,
  COUNTRY_OPTIONS,
  EVENT_LABELS,
  LINE_OPTIONS,
  STATUS_LABELS,
  errorMessage,
  formatMoney,
  formatPct,
  quoteActions,
  reasonLabel,
  LOST_REASON_OPTIONS,
  CHANGE_REASON_OPTIONS,
  proposalWarningMessage
} from '../utils/b2b-quotes.utils';

export default {
  name: 'CotizacionDetalle',
  components: { QuoteResult },
  mixins: [utilsMixin],
  data() {
    return {
      quote: null,
      parameters: null,
      executives: [],
      loading: false,
      working: false,
      lostOpen: false,
      lostReason: null,
      lostComment: '',
      lostReasons: LOST_REASON_OPTIONS,
      editOpen: false,
      changeReason: null,
      changeComment: '',
      changeReasons: CHANGE_REASON_OPTIONS,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: ''
    };
  },
  computed: {
    actions() {
      return quoteActions(this.quote?.status);
    },
    /** A newer draft of this quote, if the executive already started one. */
    openDraft() {
      if (!this.quote || this.quote.status === 'draft') return null;
      return this.quote.versions.find((v) => v.status === 'draft' && v.id !== this.quote.id) ?? null;
    },
    catalog() {
      const services = this.parameters?.parameters.services ?? [];
      return services.filter((s) => s.country === this.quote?.countryIso);
    },
    poolPctText() {
      const pool = this.quote?.input.poolSize;
      if (!pool || !this.quote.quantity) return '';
      return `${Math.round((pool / this.quote.quantity) * 100)}%`;
    },
    collectiveLines() {
      return (this.quote?.lines ?? []).filter((l) => l.modality !== 'pool');
    },
    poolLines() {
      return (this.quote?.lines ?? []).filter((l) => l.modality === 'pool');
    },
    parameterVersionLabel() {
      if (this.parameters && this.quote?.parameterVersionId === this.parameters.id) {
        return `versión ${this.parameters.version}`;
      }
      return 'de una versión anterior';
    }
  },
  watch: {
    '$route.params.id'() {
      this.load();
    }
  },
  async created() {
    const [params, executives] = await Promise.all([
      B2bQuotesService.getActiveParameters(),
      EjecutivosService.getAll(true)
    ]);
    if (params.status === 'ok') this.parameters = params.data;
    if (executives.status === 'ok') this.executives = executives.data;
    await this.load();
    this.warnIfNoteFailed();
  },
  methods: {
    can,
    canOnRecord,
    /** The Cotizador lands here after "Generar Propuesta"; Pipedrive warnings travel in the URL. */
    warnIfNoteFailed() {
      const message = proposalWarningMessage((this.$route.query.aviso || '').split(','));
      if (!message) return;
      this.showWarningSnackbar(message);
      this.$router.replace({ query: {} });
    },
    async load() {
      this.loading = true;
      const res = await B2bQuotesService.get(this.$route.params.id);
      this.loading = false;
      if (res.status === 'ok') this.quote = res.data;
      else this.showErrorSnackbar(errorMessage(res.code));
    },
    async run(request, successMessage) {
      this.working = true;
      const res = await request();
      this.working = false;
      if (res.status !== 'ok') {
        this.showErrorSnackbar(errorMessage(res.code));
        return null;
      }
      this.showSuccessSnackbar(successMessage);
      return res.data;
    },
    /** Generates the Google Slides proposal of the draft and sends it. */
    async generateProposal() {
      this.working = true;
      const res = await B2bQuotesService.generateProposal(this.quote.id);
      this.working = false;
      if (res.status !== 'ok') {
        this.showErrorSnackbar(errorMessage(res.code));
        return;
      }
      const warning = proposalWarningMessage(res.meta?.warnings);
      if (warning) {
        this.showWarningSnackbar(warning);
      } else {
        this.showSuccessSnackbar('Propuesta generada');
      }
      await this.load();
    },
    async markLost() {
      const quote = await this.run(
        () => B2bQuotesService.changeStatus(this.quote.id, 'lost', this.lostReason, this.lostComment.trim() || null),
        'Marcada como perdida'
      );
      this.lostOpen = false;
      this.lostReason = null;
      this.lostComment = '';
      if (quote) await this.load();
    },
    async changeStatus(status) {
      const quote = await this.run(
        () => B2bQuotesService.changeStatus(this.quote.id, status),
        `Marcada como ${STATUS_LABELS[status].toLowerCase()}`
      );
      if (quote) await this.load();
    },
    async newVersion() {
      const quote = await this.run(
        () => B2bQuotesService.createVersion(this.quote.id, this.changeReason, this.changeComment.trim() || null),
        'Versión nueva lista para editar'
      );
      this.editOpen = false;
      if (!quote) return;
      // Without the Cotizador's edit tag the new version opens in this detail instead.
      this.$router.push(can('quote-builder:update') ? `/cotizador/${quote.id}` : `/cotizaciones/${quote.id}`);
    },
    reasonText(event) {
      const prefix = event.type === 'created' ? 'El cliente pidió cambiar' : 'Motivo';
      return `${prefix}: ${reasonLabel(event.reason)}`;
    },
    statusLabel(status) {
      return STATUS_LABELS[status] ?? status;
    },
    eventLabel(event) {
      if (event.type === 'status_changed') return `${STATUS_LABELS[event.toStatus] ?? event.toStatus}`;
      return EVENT_LABELS[event.type] ?? event.type;
    },
    channelLabel(channel) {
      return CHANNEL_LABELS[channel] ?? channel;
    },
    countryLabel(iso) {
      return COUNTRY_OPTIONS.find((c) => c.value === iso)?.text ?? iso;
    },
    lineLabel(line) {
      return LINE_OPTIONS.find((l) => l.value === line)?.text ?? line;
    },
    executiveName(id) {
      const executive = this.executives.find((e) => e.id === id);
      return executive ? `${executive.name} ${executive.surname ?? ''}`.trim() : '—';
    },
    rate(currency) {
      const rate = this.quote.exchangeRates?.rates?.[currency];
      if (!rate) return '—';
      if (currency === 'CLP') return `${Math.round(rate.perUsd).toLocaleString('es-CL')} (${rate.asOf})`;
      const clpPerUnit = this.quote.exchangeRates.rates.CLP.perUsd / rate.perUsd;
      return `${clpPerUnit.toFixed(1).replace('.', ',')} CLP (${rate.asOf})`;
    },
    money(amount) {
      return formatMoney(amount, this.quote?.currency);
    },
    pct(value) {
      return formatPct(value);
    }
  }
};
</script>

<style scoped>
.detalle__header {
  gap: 4px;
}
</style>
